import React, { useState, useEffect, useRef } from 'react';
import './HistoryPage.css';
import DataFilters from './DataFilters';
import { useNavigate, useLocation } from 'react-router-dom';
import ChangePassword from './ChangePassword';

const HistoryPage = () => {
  // 页面视图状态
  const [currentView, setCurrentView] = useState('history'); // 'history', 'ipConfig', 'manage'
  const navigate = useNavigate();
  const location = useLocation();
  const [authValidating, setAuthValidating] = useState(true);
  const [authValid, setAuthValid] = useState(false);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const [logoutCountdown, setLogoutCountdown] = useState(5);
  const authTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);

  // 排序配置
  const [sortConfig, setSortConfig] = useState({
    key: 'create_time', // 默认按认证时间排序
    direction: 'desc'   // 默认降序（最新在前）
  });

  // 初始筛选条件（历史记录页）
  const initialFilters = {
    startTime: '',
    endTime: '',
    msg: '',
    rstCode: 'all',
    page: 1,
    pageSize: 10
  };

  // 数据管理页查询参数（整合筛选+分页）
  const [manageQueryParams, setManageQueryParams] = useState({
    stuNum: '',    // 学号
    name: '',      // 姓名
    idCard: '',    // 身份证号
    phone: '',     // 手机号
    rstCode: 'all',// 认证结果（all/0/1/2）
    pageNum: 1,    // 当前页码
    pageSize: 10   // 每页条数
  });

  // 筛选条件状态（历史记录页）
  const [filters, setFilters] = useState(initialFilters);
  const [pageInput, setPageInput] = useState('');
  const [managePageInput, setManagePageInput] = useState('');
  const [isSearchTriggered, setIsSearchTriggered] = useState(false);
  const [searchMsg, setSearchMsg] = useState(''); // 历史页独立关键字状态

  // 数据状态
  const [allData, setAllData] = useState([]);
  const [currentPageData, setCurrentPageData] = useState([]);
  const [managePageData, setManagePageData] = useState([]); // 管理页当前页数据（接口返回）
  const [total, setTotal] = useState(0);
  const [manageTotal, setManageTotal] = useState(0); // 管理页总条数（接口返回）
  const [loading, setLoading] = useState(true);
  const [manageLoading, setManageLoading] = useState(true);
  const [error, setError] = useState(null);
  const [manageError, setManageError] = useState(null);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [username, setUsername] = useState('');
  const [filteredManageTotal, setFilteredManageTotal] = useState(0); // 管理页筛选后总条数
  const [faceverCount, setFaceverCount] = useState(0); // 存储接口返回的调用次数
  const [faceverLoading, setFaceverLoading] = useState(false); // 接口加载状态
  const [faceverError, setFaceverError] = useState(null); // 接口错误状态

  // 导出状态
  const [exporting, setExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState('');

  // IP限制配置状态
  const [ipConfig, setIpConfig] = useState({
    id: '',
    stuName: { restrictType: '1', value: '' }, // 学号配置
    name: { restrictType: '1', value: '' },    // 姓名配置
    idCard: { restrictType: '1', value: '' },  // 身份证配置
    timeSpan: '',  // 认证次数限制
    times: "",
    ipTimes: '' // IP尝试次数限制
  });
  const [ipConfigLoading, setIpConfigLoading] = useState(true);
  const [ipConfigError, setIpConfigError] = useState(null);
  const [ipConfigMessage, setIpConfigMessage] = useState('');

  // 数据管理页操作状态
  const [isEditing, setIsEditing] = useState(false);
  const [currentEditRecord, setCurrentEditRecord] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newRecord, setNewRecord] = useState({
    stuNum: '',  // 学号
    name: '',    // 姓名
    idCard: ''   // 身份证号
  });
  const [manageAllData, setManageAllData] = useState([]);
  // 新增：全量数据请求loading（用于导出时的状态提示）
  const [manageAllDataLoading, setManageAllDataLoading] = useState(false);

  // 授权验证
  const validateAuthorizationCode = () => {
    try {
      let storedAuth = null;
      try {
        const stored = sessionStorage.getItem('authCode');
        storedAuth = stored ? JSON.parse(stored) : null;
      } catch (e) {
        console.error('解析授权码失败:', e);
      }

      if (!storedAuth || typeof storedAuth !== 'object') {
        setShowSessionExpiredModal(true);
        return { isValid: false };
      }

      const currentTime = Date.now();
      if (currentTime > storedAuth.expiration) {
        setShowSessionExpiredModal(true);
        return { isValid: false };
      }

      const remainingTime = storedAuth.expiration - currentTime;
      return {
        isValid: true,
        expiration: storedAuth.expiration,
        remainingTime
      };
    } catch (error) {
      console.error('授权验证失败:', error);
      return { isValid: false };
    }
  };

  // 授权过期定时器设置
  const setupAuthExpirationCheck = (expiration) => {
    if (authTimerRef.current) {
      clearTimeout(authTimerRef.current);
    }

    const remainingTime = expiration - Date.now();
    authTimerRef.current = setTimeout(() => {
      setShowSessionExpiredModal(true);
      let countdown = 5;
      setLogoutCountdown(countdown);

      countdownTimerRef.current = setInterval(() => {
        countdown -= 1;
        setLogoutCountdown(countdown);

        if (countdown <= 0) {
          clearInterval(countdownTimerRef.current);
          sessionStorage.removeItem('authCode');
          navigate('/login', { replace: true });
        }
      }, 1000);
    }, remainingTime);
  };

  // 立即跳转登录页
  const redirectToLoginImmediately = () => {
    if (authTimerRef.current) clearTimeout(authTimerRef.current);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);

    sessionStorage.removeItem('authCode');
    navigate('/login', { replace: true });
  };

  // 认证状态映射
  const statusMap = {
    0: '未认证',
    1: '成功',
    2: '失败'
  };

  // 认证状态颜色映射
  const statusColorMap = {
    2: { background: 'rgba(231, 76, 60, 0.1)', color: '#c0392b', border: '1px solid #e74c3c' },
    1: { background: 'rgba(46, 204, 113, 0.1)', color: '#27ae60', border: '1px solid #2ecc71' },
    0: { background: 'rgba(241, 196, 15, 0.1)', color: '#d35400', border: '1px solid #f39c12' }
  };

  // 获取所有认证历史数据（历史记录页）
  const fetchAllData = async () => {
    setLoading(true);
    setError(null);

    try {
      const requestBody = {
        startTime: '',
        endTime: '',
        msg: '',
        rstCode: '',
        pageNum: 1,
        pageSize: 1000000 // 全量获取
      };

      const response = await fetch('http://10.40.93.94:8077/api/auth-logs/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`请求失败，状态码: ${response.status}`);
      }

      const result = await response.json();
      if (result.code !== 0) {
        throw new Error(`API错误: ${result.msg}`);
      }

      setAllData(result.data || []);
      setTotal(result.total || 0);

    } catch (err) {
      setError(err.message);
      console.error('获取历史数据失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // 获取数据管理页数据（带筛选+分页参数）
  const fetchManageData = async () => {
    setManageLoading(true);
    setManageError(null);

    try {
      // 处理认证结果参数（all转为空）
      const requestParams = {
        ...manageQueryParams,
        rstCode: manageQueryParams.rstCode === 'all' ? '' : manageQueryParams.rstCode
      };

      const response = await fetch('http://10.40.93.94:8077/api/stu/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestParams) // 传递筛选+分页参数
      });

      if (!response.ok) {
        throw new Error(`请求失败，状态码: ${response.status}`);
      }

      const result = await response.json();
      if (result.code !== 0) {
        throw new Error(`API错误: ${result.msg}`);
      }

      // 处理接口返回数据（默认格式：{ data: { list: [], total: 0 }, total: 0 }）
      const { list = [], total = 0 } = result.data || {};
      // 过滤已删除记录（delFlag=2）
      const validData = list.filter(item => item.delFlag !== 2);

      setManagePageData(validData); // 接口返回的当前页数据
      setManageTotal(total);        // 接口返回的总条数
      setFilteredManageTotal(total); // 筛选后总条数同步

    } catch (err) {
      setManageError(err.message);
      console.error('获取管理数据失败:', err);
    } finally {
      setManageLoading(false);
    }
  };
  const fetchManageAllData = async () => {
    setManageAllDataLoading(true); // 开启全量数据请求loading
    setManageError(null);
    try {
      // 复用当前筛选条件，但强制分页参数为“第1页+极大页大小”（获取全量）
      const fullParams = {
        ...manageQueryParams,
        rstCode: manageQueryParams.rstCode === 'all' ? '' : manageQueryParams.rstCode,
        pageNum: 1,          // 从第1页开始
        pageSize: 100000     // 极大页大小，确保覆盖所有数据
      };

      const response = await fetch('http://10.40.93.94:8077/api/stu/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fullParams)
      });

      if (!response.ok) throw new Error(`请求失败，状态码: ${response.status}`);
      const result = await response.json();
      if (result.code !== 0) throw new Error(`API错误: ${result.msg}`);

      // 过滤已删除记录（同原有逻辑），并存储全量数据
      const { list = [] } = result.data || {};
      const validFullData = list.filter(item => item.delFlag !== 2);
      setManageAllData(validFullData); // 保存全量数据
      return validFullData; // 返回全量数据，供导出使用

    } catch (err) {
      setManageError(err.message);
      console.error('获取管理页全量数据失败:', err);
      throw err; // 抛出错误，让导出函数捕获并提示

    } finally {
      setManageAllDataLoading(false); // 关闭loading
    }
  };
  // 获取IP配置数据
  const fetchIpConfig = async () => {
    setIpConfigLoading(true);
    setIpConfigError(null);

    try {
      const response = await fetch('http://10.40.93.94:8077/api/conf/get', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: "1" }) // 固定ID
      });

      if (!response.ok) {
        throw new Error(`请求失败，状态码: ${response.status}`);
      }

      const result = await response.json();
      if (result.code !== 0) {
        throw new Error(`API错误: ${result.msg}`);
      }

      const configData = result.data;
      setIpConfig({
        id: configData.id || '',
        stuName: {
          restrictType: String(configData.stuName) === '0' ? '0' : '1',
          value: ''
        },
        name: {
          restrictType: String(configData.name) === '0' ? '0' : '1',
          value: ''
        },
        idCard: {
          restrictType: String(configData.idCard) === '0' ? '0' : '1',
          value: ''
        },
        timeSpan: configData.timeSpan || '',
        times: configData.times || "",
        ipTimes: configData.ipTimes || ''
      });

    } catch (err) {
      setIpConfigError(err.message);
      console.error('获取IP配置失败:', err);
    } finally {
      setIpConfigLoading(false);
    }
  };

  
  async function fetchFaceverData() {
  setFaceverLoading(true);
  setFaceverError(null);
  try {
    const response = await fetch('http://59.56.207.14:8077/api/auth-logs/count-facever', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });

    const { code, msg, data } = await response.json();

    if (code === 0) {
      setFaceverCount(data || 0); // 存储接口返回的调用次数
      return data;
    } else {
      console.error('人脸验证接口统计失败:', msg);
      setFaceverError(msg);
      setFaceverCount(0);
      return null;
    }
  } catch (error) {
    console.error('人脸验证接口请求异常:', error);
    setFaceverError(error.message);
    setFaceverCount(0);
    return null;
  } finally {
    setFaceverLoading(false); // 结束加载状态
  }
}

  // 组件挂载与参数变化时加载数据
  useEffect(() => {
  const authResult = validateAuthorizationCode();

  if (!authResult.isValid) {
    // 原有过期逻辑（无需修改）
    let countdown = 5;
    setLogoutCountdown(countdown);
    setShowSessionExpiredModal(true);

    const timer = setInterval(() => {
      countdown -= 1;
      setLogoutCountdown(countdown);

      if (countdown <= 0) {
        clearInterval(timer);
        sessionStorage.removeItem('authCode');
        navigate('/login', { replace: true });
      }
    }, 1000);

    return () => clearInterval(timer);
  } else {
    setAuthValid(true);
    setAuthValidating(false);
    setupAuthExpirationCheck(authResult.expiration);

    // 加载用户信息与初始数据
    const storedUsername = localStorage.getItem('username');
    if (storedUsername) {
      setUsername(storedUsername);
    }
    fetchAllData();
    fetchManageData(); // 首次加载管理页数据
    fetchIpConfig();
    fetchFaceverData(); // 新增：调用人脸验证接口统计函数
  }

  // 组件卸载清理
  return () => {
    if (authTimerRef.current) {
      clearTimeout(authTimerRef.current);
    }
  };
}, [navigate, location, manageQueryParams]);// 管理页参数变化时重新请求

  // 历史记录页：筛选条件变化
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1
    }));
  };

  // 历史记录页：关键字变化
  const handleKeywordChange = (e) => {
    setSearchMsg(e.target.value);
  };

  // 数据管理页：筛选条件变化
  const handleManageFilterChange = (e) => {
    const { name, value } = e.target;
    setManageQueryParams(prev => ({
      ...prev,
      [name]: value,
      pageNum: 1 // 筛选变化重置页码
    }));
  };

  // 数据管理页：每页条数变化
  const handleManagePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value);
    setManageQueryParams(prev => ({
      ...prev,
      pageSize: newSize,
      pageNum: 1 // 条数变化重置页码
    }));
  };

  // 历史记录页：分页变化
  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  // 数据管理页：分页变化
  const handleManagePageChange = (newPage) => {
    setManageQueryParams(prev => ({ ...prev, pageNum: newPage }));
  };

  // 历史记录页：每页数量变化
  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value);
    setFilters(prev => ({ ...prev, pageSize: newSize, page: 1 }));
  };

  // 历史记录页：搜索触发
  const handleSearchClick = () => {
    setIsSearchTriggered(true);
    setFilters(prev => ({ ...prev, page: 1 }));
  };

  // 历史记录页：重置筛选
  const resetFilters = () => {
    setFilters(initialFilters);
    setSearchMsg('');
    setIsSearchTriggered(false);
  };

  // 数据管理页：重置筛选
  const resetManageFilters = () => {
    setManageQueryParams({
      stuNum: '',
      name: '',
      idCard: '',
      phone: '',
      rstCode: 'all',
      pageNum: 1,
      pageSize: 10
    });
    setManagePageInput('');
  };

  // 历史记录页：页码输入变化
  const handlePageInputChange = (e) => {
    setPageInput(e.target.value);
  };

  // 数据管理页：页码输入变化
  const handleManagePageInputChange = (e) => {
    setManagePageInput(e.target.value);
  };

  // 历史记录页：页码跳转
  const handlePageJump = () => {
    const pageNum = parseInt(pageInput);
    const totalPages = Math.ceil(allData.length / filters.pageSize) || 1;

    if (!isNaN(pageNum)) {
      const validPage = Math.min(Math.max(1, pageNum), totalPages);
      setFilters(prev => ({ ...prev, page: validPage }));
      setPageInput('');
    }
  };

  // 数据管理页：页码跳转
  const handleManagePageJump = () => {
    const pageNum = parseInt(managePageInput);
    const totalPages = Math.ceil(manageTotal / manageQueryParams.pageSize) || 1;

    if (!isNaN(pageNum)) {
      const validPage = Math.min(Math.max(1, pageNum), totalPages);
      setManageQueryParams(prev => ({ ...prev, pageNum: validPage }));
      setManagePageInput('');
    }
  };

  // 历史记录页：回车跳转
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handlePageJump();
    }
  };

  // 数据管理页：回车跳转
  const handleManageKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleManagePageJump();
    }
  };

  // 历史记录页：排序请求
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // 历史记录页：应用筛选
  const applyFilters = () => {
    if (loading || allData.length === 0) return [];

    let filteredData = [...allData];

    // 时间筛选
    if (filters.startTime) {
      const startDate = new Date(filters.startTime);
      filteredData = filteredData.filter(item =>
        new Date(item.create_time) >= startDate
      );
    }

    if (filters.endTime) {
      const endDate = new Date(filters.endTime);
      endDate.setHours(23, 59, 59, 999);
      filteredData = filteredData.filter(item =>
        new Date(item.create_time) <= endDate
      );
    }

    // 关键字筛选
    if (isSearchTriggered && searchMsg) {
      const msg = searchMsg.toLowerCase();
      filteredData = filteredData.filter(item =>
        (item.stuNum && item.stuNum.toLowerCase().includes(msg)) ||
        (item.name && item.name.toLowerCase().includes(msg)) ||
        (item.phone && item.phone.toLowerCase().includes(msg))
      );
    }

    // 认证结果筛选
    if (filters.rstCode !== 'all') {
      filteredData = filteredData.filter(item =>
        String(item.rstCode) === filters.rstCode
      );
    }

    // 排序
    if (sortConfig.key) {
      filteredData.sort((a, b) => {
        const valueA = a[sortConfig.key];
        const valueB = b[sortConfig.key];

        if (sortConfig.key === 'create_time') {
          const timeA = new Date(valueA).getTime();
          const timeB = new Date(valueB).getTime();
          return sortConfig.direction === 'asc' ? timeA - timeB : timeB - timeA;
        }

        return sortConfig.direction === 'asc'
          ? (valueA < valueB ? -1 : 1)
          : (valueA > valueB ? -1 : 1);
      });
    }

    setFilteredTotal(filteredData.length);
    return filteredData;
  };

  // 数据管理页：应用筛选（接口已处理，前端仅做显示）
  const applyManageFilters = () => {
    if (manageLoading || managePageData.length === 0) return [];
    setFilteredManageTotal(manageTotal);
    return managePageData;
  };

  // 历史记录页：应用分页
  const applyPagination = (filteredData) => {
    const filteredTotal = filteredData.length;
    const startIndex = (filters.page - 1) * filters.pageSize;
    const endIndex = Math.min(startIndex + filters.pageSize, filteredTotal);
    setCurrentPageData(filteredData.slice(startIndex, endIndex));
  };

  // 历史记录页：筛选/分页变化时更新数据
  useEffect(() => {
    const filteredData = applyFilters();
    applyPagination(filteredData);
  }, [filters, allData, sortConfig]);

  // 数据管理页：筛选/分页变化时更新数据（依赖接口返回）
  useEffect(() => {
    applyManageFilters();
  }, [managePageData, manageTotal]);

  // 历史记录页：导出CSV
  const exportToCSV = () => {
    setExporting(true);
    setExportMessage('正在准备导出数据...');

    try {
      const filteredData = applyFilters();
      if (filteredData.length === 0) {
        setExportMessage('没有数据可导出');
        setTimeout(() => setExportMessage(''), 3000);
        setExporting(false);
        return;
      }

      const headers = ['序号', '姓名', '学号', '手机号', '认证结果', '认证时间', 'IP地址'];
      const rows = filteredData.map((record, index) => [
        index + 1,
        record.name || '-',
        record.stuNum || '-',
        record.phone || '-',
        statusMap[record.rstCode] || '未知状态',
        record.create_time || '-',
        record.ip || '-'
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row =>
          row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

      link.setAttribute('href', url);
      link.setAttribute('download', `认证记录_${timestamp}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setExportMessage('导出成功！文件已开始下载');
      setTimeout(() => setExportMessage(''), 3000);
    } catch (err) {
      console.error('导出失败:', err);
      setExportMessage(`导出失败: ${err.message}`);
      setTimeout(() => setExportMessage(''), 5000);
    } finally {
      setExporting(false);
    }
  };

  // 数据管理页：导出CSV
  const exportManageToCSV = async () => {
    setExporting(true);
    setExportMessage('正在获取全量数据...'); // 提示“获取全量数据”
    try {
      // 步骤1：先请求筛选后的全量数据
      const fullFilteredData = await fetchManageAllData();
      if (fullFilteredData.length === 0) {
        setExportMessage('没有数据可导出');
        setTimeout(() => setExportMessage(''), 3000);
        return;
      }

      // 步骤2：基于全量数据生成CSV（表头和字段逻辑不变）
      const headers = ['序号', '学号', '姓名', '身份证号', '手机号', '验证日期', '验证信息'];
      const rows = fullFilteredData.map((record, index) => [
        index + 1, // 序号（全量数据的连续序号）
        record.stuNum || '-',
        record.name || '-',
        record.idCard || '-',
        record.phone || '-',
        record.rstTime || '-',
        record.rstMsg || '-'
      ]);

      // 步骤3：生成CSV文件并触发下载（同原有逻辑）
      const csvContent = [
        headers.join(','),
        ...rows.map(row =>
          row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      link.setAttribute('href', url);
      link.setAttribute('download', `配置数据_${timestamp}.csv`); // 保持原文件名格式
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // 导出成功提示
      setExportMessage('导出成功！文件已开始下载（共 ' + fullFilteredData.length + ' 条记录）');
      setTimeout(() => setExportMessage(''), 3000);

    } catch (err) {
      // 捕获全量数据请求或导出过程中的错误
      setExportMessage(`导出失败: ${err.message}`);
      setTimeout(() => setExportMessage(''), 5000);
      console.error('管理页数据导出失败:', err);

    } finally {
      setExporting(false); // 关闭导出loading
    }
  };

  // 历史记录页：状态统计
  const calculateStatusCounts = () => {
    if (allData.length === 0) return { success: 0, failed: 0, unauthenticated: 0 };

    return {
      unauthenticated: allData.filter(d => d.rstCode === 0).length,
      success: allData.filter(d => d.rstCode === 1).length,
      failed: allData.filter(d => d.rstCode === 2).length
    };
  };

  const statusCounts = calculateStatusCounts();

  // 分页计算
  const totalPages = Math.ceil(filteredTotal / filters.pageSize) || 1;
  const manageTotalPages = Math.ceil(manageTotal / manageQueryParams.pageSize) || 1;

  // IP配置：输入变化
  const handleIpConfigChange = (e) => {
    const { name, value } = e.target;
    setIpConfig(prev => ({ ...prev, [name]: value }));
  };

  const handleFieldValueChange = (field, value) => {
    setIpConfig(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        value
      }
    }));
  };

  const handleRestrictTypeChange = (field, restrictType) => {
    setIpConfig(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        restrictType
      }
    }));
  };

  // IP配置：提交
  const submitIpConfig = async () => {
    setExporting(true);
    setIpConfigMessage('正在提交IP限制配置...');

    try {
      const requestData = {
        id: 1,
        stuName: ipConfig.stuName.restrictType,
        name: ipConfig.name.restrictType,
        idCard: ipConfig.idCard.restrictType,
        timeSpan: ipConfig.timeSpan,
        times: ipConfig.times,
        ipTimes: ipConfig.ipTimes,
      };

      const response = await fetch('http://10.40.93.94:8077/api/conf/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        throw new Error(`请求失败，状态码: ${response.status}`);
      }

      const result = await response.json();
      if (result.code !== 0) {
        throw new Error(`配置更新失败: ${result.msg}`);
      }

      setIpConfigMessage('IP限制配置更新成功！');
    } catch (err) {
      console.error('IP配置失败:', err);
      setIpConfigMessage(`配置失败: ${err.message}`);
    } finally {
      setExporting(false);
      setTimeout(() => setIpConfigMessage(''), 5000);
    }
  };

  // 数据管理页：新增记录输入变化
  const handleNewRecordChange = (e) => {
    const { name, value } = e.target;
    setNewRecord(prev => ({ ...prev, [name]: value }));
  };

  // 数据管理页：编辑记录输入变化
  const handleEditRecordChange = (e) => {
    const { name, value } = e.target;
    setCurrentEditRecord(prev => ({ ...prev, [name]: value }));
  };

  // 数据管理页：打开编辑
  const handleEdit = (record) => {
    setCurrentEditRecord({ ...record });
    setIsEditing(true);
  };

  // 数据管理页：取消编辑
  const cancelEdit = () => {
    setIsEditing(false);
    setCurrentEditRecord(null);
  };

  // 数据管理页：打开新增
  const handleAddNew = () => {
    setShowAddForm(true);
  };

  // 数据管理页：取消新增
  const cancelAdd = () => {
    setShowAddForm(false);
    setNewRecord({
      stuNum: '',
      name: '',
      idCard: ''
    });
  };

  // 数据管理页：提交新增
  const submitNewRecord = async () => {
    try {
      const response = await fetch('http://10.40.93.94:8077/api/stu/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          stuNum: newRecord.stuNum,
          name: newRecord.name,
          idCard: newRecord.idCard
        })
      });

      if (!response.ok) {
        throw new Error(`请求失败，状态码: ${response.status}`);
      }

      const result = await response.json();
      if (result.code !== 0) {
        throw new Error(`新增失败: ${result.msg}`);
      }

      fetchManageData(); // 刷新数据
      setShowAddForm(false);
      setNewRecord({ stuNum: '', name: '', idCard: '' });
      alert('新增记录成功！');
    } catch (err) {
      console.error('新增记录失败:', err);
      alert(`新增失败: ${err.message}`);
    }
  };

  // 数据管理页：提交编辑
  const submitEditRecord = async () => {
    try {
      const response = await fetch('http://10.40.93.94:8077/api/stu/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: currentEditRecord.id,
          stuNum: currentEditRecord.stuNum,
          name: currentEditRecord.name,
          idCard: currentEditRecord.idCard
        })
      });

      if (!response.ok) {
        throw new Error(`请求失败，状态码: ${response.status}`);
      }

      const result = await response.json();
      if (result.code !== 0) {
        throw new Error(`更新失败: ${result.msg}`);
      }

      fetchManageData(); // 刷新数据
      setIsEditing(false);
      setCurrentEditRecord(null);
      alert('更新记录成功！');
    } catch (err) {
      console.error('更新记录失败:', err);
      alert(`更新失败: ${err.message}`);
    }
  };

  // 数据管理页：删除记录
  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除这条记录吗？')) return;

    try {
      const response = await fetch('http://10.40.93.94:8077/api/stu/delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id })
      });

      if (!response.ok) {
        throw new Error(`请求失败，状态码: ${response.status}`);
      }

      const result = await response.json();
      if (result.code !== 0) {
        throw new Error(`删除失败: ${result.msg}`);
      }

      fetchManageData(); // 刷新数据
      alert('删除记录成功！');
    } catch (err) {
      console.error('删除记录失败:', err);
      alert(`删除失败: ${err.message}`);
    }
  };

  // 登出
  const handleLogout = () => {
    if (authTimerRef.current) clearTimeout(authTimerRef.current);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);

    sessionStorage.removeItem('authCode');
    localStorage.removeItem('authToken');
    navigate('/login', { replace: true });
  };

  // 授权验证中
  if (authValidating) {
    return (
      <div className="auth-validating">
        <i className="fas fa-spinner fa-spin"></i>
        <p>正在验证访问权限...</p>
      </div>
    );
  }

  // 授权无效
  if (!authValid) {
    return (
      <div className="auth-error">
        <i className="fas fa-exclamation-triangle"></i>
        <h3>访问未授权或会话已过期</h3>
        <p>您将被重定向到登录页面</p>
        <button className="retry-btn" onClick={() => navigate('/login')}>
          <i className="fas fa-sign-in-alt"></i> 立即登录
        </button>
      </div>
    );
  }

  // 打开修改密码
  const handleChangePassword = () => {
    setShowChangePassword(true);
    setIsUserMenuOpen(false);
  };

  // 关闭修改密码
  const handleCloseChangePassword = () => {
    setShowChangePassword(false);
  };

  // 渲染历史记录页
  const renderHistoryPage = () => (
    <>
      <div className="header">
        <img src="/logo.png" alt="泉州师范学院" className="header-logo" />
        <h1> 历史记录查询</h1>
        <span className="facever-count-tag">
          {faceverLoading 
            ? '接口调用次数：加载中/50000' 
            : faceverError 
              ? '接口调用次数：获取失败/50000' 
              : `接口调用次数：${faceverCount}/50000`}
        </span>
      </div>

      <div className="filters-container">
        <div className="filter-row">
          <div className="filter-group">
            <label htmlFor="startTime"><i className="fas fa-calendar-start"></i> 起始时间</label>
            <input
              type="datetime-local"
              id="startTime"
              name="startTime"
              value={filters.startTime}
              onChange={handleFilterChange}
            />
          </div>

          <div className="filter-group">
            <label htmlFor="endTime"><i className="fas fa-calendar-end"></i> 终止时间</label>
            <input
              type="datetime-local"
              id="endTime"
              name="endTime"
              value={filters.endTime}
              onChange={handleFilterChange}
            />
          </div>

          <div className="filter-group">
            <label htmlFor="msg"><i className="fas fa-id-card"></i> 学号/姓名/手机号查询</label>
            <input
              type="text"
              id="msg"
              name="msg"
              placeholder="输入学号/姓名/手机号"
              value={searchMsg}
              onChange={handleKeywordChange}
            />
          </div>
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <label htmlFor="rstCode"><i className="fas fa-check-circle"></i> 认证结果</label>
            <select
              id="rstCode"
              name="rstCode"
              value={filters.rstCode}
              onChange={handleFilterChange}
            >
              <option value="all">全部状态</option>
              <option value="0">未认证</option>
              <option value="1">认证成功</option>
              <option value="2">认证失败</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="pageSize"><i className="fas fa-list-ol"></i> 每页显示</label>
            <select
              id="pageSize"
              name="pageSize"
              value={filters.pageSize}
              onChange={handlePageSizeChange}
            >
              <option value="5">5条</option>
              <option value="10">10条</option>
              <option value="20">20条</option>
              <option value="50">50条</option>
            </select>
          </div>

          <div className="filter-actions">
            <button
              className="search-btn"
              onClick={handleSearchClick}
            >
              <i className="fas fa-search"></i> 查询
            </button>
            <button className="reset-btn" onClick={resetFilters}>
              <i className="fas fa-redo"></i> 重置
            </button>
            <button
              className="export-btn"
              onClick={exportToCSV}
              disabled={exporting}
            >
              {exporting ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> 导出中...
                </>
              ) : (
                <>
                  <i className="fas fa-file-export"></i> 导出数据
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {exportMessage && (
        <div className={`export-message ${exportMessage.includes('成功') ? 'success' : 'error'}`}>
          {exportMessage}
        </div>
      )}

      <div className="table-container">
        {error ? (
          <div className="error-container">
            <i className="fas fa-exclamation-triangle"></i>
            <p>数据加载失败: {error}</p>
            <button className="retry-btn" onClick={fetchAllData}>
              <i className="fas fa-redo"></i> 重新加载
            </button>
          </div>
        ) : loading ? (
          <div className="loading-container">
            <i className="fas fa-spinner fa-spin"></i>
            <p>正在加载数据...</p>
          </div>
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th>序号</th>
                  <th>姓名</th>
                  <th>学号</th>
                  <th>手机号</th>
                  <th>认证结果</th>
                  <th
                    onClick={() => requestSort('create_time')}
                    style={{ cursor: 'pointer' }}
                  >
                    认证时间
                    {sortConfig.key === 'create_time' && (
                      sortConfig.direction === 'asc'
                        ? <i className="fas fa-arrow-up" style={{ marginLeft: '5px' }}></i>
                        : <i className="fas fa-arrow-down" style={{ marginLeft: '5px' }}></i>
                    )}
                  </th>
                  <th>IP地址</th>
                </tr>
              </thead>
              <tbody>
                {currentPageData.length > 0 ? (
                  currentPageData.map((record, index) => {
                    const globalIndex = (filters.page - 1) * filters.pageSize + index + 1;
                    return (
                      <tr key={record.id || index}>
                        <td>{globalIndex}</td>
                        <td>{record.name || '-'}</td>
                        <td>{record.stuNum || '-'}</td>
                        <td>{record.phone || '-'}</td>
                        <td>
                          <span
                            className="status-tag"
                            style={statusColorMap[record.rstCode] || {}}
                          >
                            {statusMap[record.rstCode] || '未知状态'}
                          </span>
                        </td>
                        <td>{record.create_time || '-'}</td>
                        <td>{record.ip || '-'}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="no-data">
                      <i className="fas fa-database"></i> 未找到匹配的记录
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="pagination">
              <button
                onClick={() => handlePageChange(filters.page - 1)}
                disabled={filters.page === 1}
                className="pagination-btn"
              >
                <i className="fas fa-chevron-left"></i> 上一页
              </button>

              <div className="page-info">
                第 <strong>{filters.page}</strong> 页 / 共 <strong>{totalPages}</strong> 页
                <span className="total-records">（共 {filteredTotal} 条记录）</span>
              </div>

              <div className="page-jump">
                <span>跳转到：</span>
                <input
                  type="number"
                  min="1"
                  max={totalPages}
                  value={pageInput}
                  onChange={handlePageInputChange}
                  onKeyPress={handleKeyPress}
                  placeholder="页码"
                />
                <button
                  onClick={handlePageJump}
                  className="jump-btn"
                >
                  跳转
                </button>
              </div>

              <button
                onClick={() => handlePageChange(filters.page + 1)}
                disabled={filters.page === totalPages || totalPages === 0}
                className="pagination-btn"
              >
                下一页 <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );

  // 渲染IP配置页面
  const renderIpConfigPage = () => (
    <>
      <div className="header">
        <img src="/logo.png" alt="泉州师范学院" className="header-logo" />
        <h1> IP限制配置</h1>
      </div>

      <div className="config-form-container">
        {ipConfigError ? (
          <div className="error-container">
            <i className="fas fa-exclamation-triangle"></i>
            <p>配置加载失败: {ipConfigError}</p>
            <button className="retry-btn" onClick={fetchIpConfig}>
              <i className="fas fa-redo"></i> 重新加载
            </button>
          </div>
        ) : ipConfigLoading ? (
          <div className="loading-container">
            <i className="fas fa-spinner fa-spin"></i>
            <p>正在加载配置...</p>
          </div>
        ) : (
          <>
            {ipConfigMessage && (
              <div className={`export-message ${ipConfigMessage.includes('成功') ? 'success' : 'error'}`}>
                {ipConfigMessage}
              </div>
            )}

            {/* 学号配置 */}
            <div className="field-config-group">
              <h3 className="field-title">学/工号配置</h3>
              <div className="form-group">
                <label>限制类型</label>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      name="stuName-restrict"
                      value="1"
                      checked={ipConfig.stuName.restrictType === '1'}
                      onChange={() => handleRestrictTypeChange('stuName', '1')}
                    />
                    <span>限制</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="stuName-restrict"
                      value="0"
                      checked={ipConfig.stuName.restrictType === '0'}
                      onChange={() => handleRestrictTypeChange('stuName', '0')}
                    />
                    <span>不限制</span>
                  </label>
                </div>
              </div>
            </div>

            {/* 姓名配置 */}
            <div className="field-config-group">
              <h3 className="field-title">姓名配置</h3>
              <div className="form-group">
                <label>限制类型</label>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      name="name-restrict"
                      value="1"
                      checked={ipConfig.name.restrictType === '1'}
                      onChange={() => handleRestrictTypeChange('name', '1')}
                    />
                    <span>限制</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="name-restrict"
                      value="0"
                      checked={ipConfig.name.restrictType === '0'}
                      onChange={() => handleRestrictTypeChange('name', '0')}
                    />
                    <span>不限制</span>
                  </label>
                </div>
              </div>
            </div>

            {/* 身份证配置 */}
            <div className="field-config-group">
              <h3 className="field-title">身份证配置</h3>
              <div className="form-group">
                <label>限制类型</label>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      name="idCard-restrict"
                      value="1"
                      checked={ipConfig.idCard.restrictType === '1'}
                      onChange={() => handleRestrictTypeChange('idCard', '1')}
                    />
                    <span>限制</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="idCard-restrict"
                      value="0"
                      checked={ipConfig.idCard.restrictType === '0'}
                      onChange={() => handleRestrictTypeChange('idCard', '0')}
                    />
                    <span>不限制</span>
                  </label>
                </div>
              </div>
            </div>

            {/* 通用配置 */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="timeSpan">同一时间限制（小时）</label>
                <input
                  type="number"
                  id="timeSpan"
                  name="timeSpan"
                  value={ipConfig.timeSpan}
                  onChange={handleIpConfigChange}
                  min="1"
                />
              </div>

              <div className="form-group">
                <label htmlFor="times">学/工号、姓名、身份证限制次数</label>
                <input
                  type="number"
                  id="times"
                  name="times"
                  value={ipConfig.times}
                  onChange={handleIpConfigChange}
                  min="1"
                />
              </div>

              <div className="form-group">
                <label htmlFor="ipTimes">IP尝试次数限制</label>
                <input
                  type="number"
                  id="ipTimes"
                  name="ipTimes"
                  value={ipConfig.ipTimes}
                  onChange={handleIpConfigChange}
                  min="1"
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                className="export-btn"
                onClick={submitIpConfig}
                disabled={exporting}
              >
                {exporting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> 提交中...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save"></i> 保存配置
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );

  // 渲染数据管理页面
  const renderManagePage = () => (
    <>
      <div className="header">
        <img src="/logo.png" alt="泉州师范学院" className="header-logo" />
        <h1> 数据管理</h1>
      </div>

      {/* 筛选组件 */}
      <DataFilters
        filters={manageQueryParams}
        onFilterChange={handleManageFilterChange}
        onReset={resetManageFilters}
        onPageSizeChange={handleManagePageSizeChange}
        onExport={exportManageToCSV}
        exporting={exporting}
        // 如需单独搜索按钮可添加 onSearch={fetchManageData}
      />

      {exportMessage && (
        <div className={`export-message ${exportMessage.includes('成功') ? 'success' : 'error'}`}>
          {exportMessage}
        </div>
      )}

      {/* 新增记录表单 */}
      {showAddForm && (
        <div className="edit-form">
          <h3><i className="fas fa-plus-circle"></i> 新增记录</h3>
          <div className="form-row">
            <div className="form-group">
              <label>学号</label>
              <input
                type="text"
                name="stuNum"
                value={newRecord.stuNum}
                onChange={handleNewRecordChange}
                placeholder="输入学号"
                required
              />
            </div>
            <div className="form-group">
              <label>姓名</label>
              <input
                type="text"
                name="name"
                value={newRecord.name}
                onChange={handleNewRecordChange}
                placeholder="输入姓名"
                required
              />
            </div>
            <div className="form-group">
              <label>身份证号</label>
              <input
                type="text"
                name="idCard"
                value={newRecord.idCard}
                onChange={handleNewRecordChange}
                placeholder="输入身份证号"
                required
              />
            </div>
          </div>
          <div className="form-actions">
            <button className="cancel-btn" onClick={cancelAdd}>
              <i className="fas fa-times"></i> 取消
            </button>
            <button className="save-btn" onClick={submitNewRecord}>
              <i className="fas fa-save"></i> 保存
            </button>
          </div>
        </div>
      )}

      {/* 编辑记录表单 */}
      {isEditing && currentEditRecord && (
        <div className="edit-form">
          <h3><i className="fas fa-edit"></i> 编辑记录</h3>
          <div className="form-row">
            <div className="form-group">
              <label>配置ID</label>
              <input
                type="text"
                name="id"
                value={currentEditRecord.id}
                onChange={handleEditRecordChange}
                disabled
              />
            </div>
            <div className="form-group">
              <label>学号</label>
              <input
                type="text"
                name="stuNum"
                value={currentEditRecord.stuNum}
                onChange={handleEditRecordChange}
                required
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>姓名</label>
              <input
                type="text"
                name="name"
                value={currentEditRecord.name}
                onChange={handleEditRecordChange}
                required
              />
            </div>
            <div className="form-group">
              <label>身份证号</label>
              <input
                type="text"
                name="idCard"
                value={currentEditRecord.idCard}
                onChange={handleEditRecordChange}
                required
              />
            </div>
          </div>
          <div className="form-actions">
            <button className="cancel-btn" onClick={cancelEdit}>
              <i className="fas fa-times"></i> 取消
            </button>
            <button className="save-btn" onClick={submitEditRecord}>
              <i className="fas fa-save"></i> 保存
            </button>
          </div>
        </div>
      )}

      <div className="table-container">
        {manageError ? (
          <div className="error-container">
            <i className="fas fa-exclamation-triangle"></i>
            <p>数据加载失败: {manageError}</p>
            <button className="retry-btn" onClick={fetchManageData}>
              <i className="fas fa-redo"></i> 重新加载
            </button>
          </div>
        ) : manageLoading ? (
          <div className="loading-container">
            <i className="fas fa-spinner fa-spin"></i>
            <p>正在加载数据...</p>
          </div>
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th>序号</th>
                  <th>学号</th>
                  <th>学生姓名</th>
                  <th>身份证号</th>
                  <th>手机号</th>
                  <th>认证日期</th>
                  <th>认证结果</th>
                  <th>
                    操作
                    <button
                      className="add-in-header"
                      onClick={handleAddNew}
                      title="新增记录"
                    >
                      <i className="fas fa-plus"></i>
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {managePageData.length > 0 ? (
                  managePageData.map((record, index) => {
                    const globalIndex = (manageQueryParams.pageNum - 1) * manageQueryParams.pageSize + index + 1;
                    return (
                      <tr key={record.id || index}>
                        <td>{globalIndex}</td>
                        <td>{record.stuNum || '-'}</td>
                        <td>{record.name || '-'}</td>
                        <td>{record.idCard || '-'}</td>
                        <td>{record.phone || '-'}</td>
                        <td>{record.rstTime || '-'}</td>
                        <td>
                          {record.rstMsg ? (
                            record.rstMsg
                          ) : (
                            <span className="status-tag" style={statusColorMap[0]}>
                              未认证
                            </span>
                          )}
                        </td>
                        <td className="actions">
                          <button
                            className="edit-btn"
                            onClick={() => handleEdit(record)}
                            title="编辑"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="delete-btn"
                            onClick={() => handleDelete(record.id)}
                            title="删除"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8" className="no-data">
                      <i className="fas fa-database"></i> 未找到匹配的记录
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="pagination">
              <button
                onClick={() => handleManagePageChange(manageQueryParams.pageNum - 1)}
                disabled={manageQueryParams.pageNum === 1 || manageLoading}
                className="pagination-btn"
              >
                <i className="fas fa-chevron-left"></i> 上一页
              </button>

              <div className="page-info">
                第 <strong>{manageQueryParams.pageNum}</strong> 页 / 共 <strong>{manageTotalPages}</strong> 页
                <span className="total-records">（共 {manageTotal} 条记录）</span>
              </div>

              <div className="page-jump">
                <span>跳转到：</span>
                <input
                  type="number"
                  min="1"
                  max={manageTotalPages}
                  value={managePageInput}
                  onChange={handleManagePageInputChange}
                  onKeyPress={handleManageKeyPress}
                  placeholder="页码"
                  disabled={manageLoading}
                />
                <button
                  onClick={handleManagePageJump}
                  className="jump-btn"
                  disabled={manageLoading}
                >
                  跳转
                </button>
              </div>

              <button
                onClick={() => handleManagePageChange(manageQueryParams.pageNum + 1)}
                disabled={manageQueryParams.pageNum === manageTotalPages || manageTotalPages === 0 || manageLoading}
                className="pagination-btn"
              >
                下一页 <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );

  // 主渲染
  return (
    <div className="history-container">
      {showSessionExpiredModal && (
        <div className="session-expired-modal">
          <h3>会话已过期</h3>
          <p>{logoutCountdown}秒后自动跳转到登录页面</p>
          <button
            className="confirm-button"
            onClick={redirectToLoginImmediately}
          >
            立即重新登录
          </button>
        </div>
      )}

      <nav className="vertical-nav">
        <ul>
          <li>
            <button
              className={currentView === 'history' ? 'active' : ''}
              onClick={() => setCurrentView('history')}
            >
              <i className="fas fa-history"></i>
              <span>历史记录查询</span>
            </button>
          </li>
          <li>
            <button
              className={currentView === 'ipConfig' ? 'active' : ''}
              onClick={() => setCurrentView('ipConfig')}
            >
              <i className="fas fa-ban"></i>
              <span>IP限制配置</span>
            </button>
          </li>
          <li>
            <button
              className={currentView === 'manage' ? 'active' : ''}
              onClick={() => setCurrentView('manage')}
            >
              <i className="fas fa-chart-bar"></i>
              <span>数据管理</span>
            </button>
          </li>
        </ul>

        <div
          className="user-profile"
          onMouseEnter={() => setIsUserMenuOpen(true)}
          onMouseLeave={() => setIsUserMenuOpen(false)}
        >
          <div className="avatar">
            <i className="fas fa-user-circle"></i>
          </div>
          {isUserMenuOpen && (
            <div className="user-menu">
              <button onClick={handleChangePassword}>
                <i className="fas fa-key"></i> 修改密码
              </button>
              <button onClick={handleLogout}>
                <i className="fas fa-sign-out-alt"></i> 登出
              </button>
            </div>
          )}
          {showChangePassword && (
            <ChangePassword
              initialUsername={username}
              onClose={handleCloseChangePassword}
              darkMode={false}
            />
          )}
        </div>
      </nav>

      <div className="main-content">
        {currentView === 'history' && renderHistoryPage()}
        {currentView === 'ipConfig' && renderIpConfigPage()}
        {currentView === 'manage' && renderManagePage()}
      </div>
    </div>
  );
};

export default HistoryPage;